/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { RssFeed } from "@mui/icons-material";
import { Box, Container, Grid, Link, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";
import PopperBlock from "../Components/PopperBlock";
import { useCloud } from "../Hooks/Cloud";

/**
 * FooterItem component
 * Description: This component is used to display the footer.
 */
function FooterItem({ link, label, variant }) {
  return (
    <Link href={ link } underline="none" variant={ variant }>
      <Typography variant={ variant }>{ label }</Typography>
    </Link>
  );
}

FooterItem.propTypes = {
  label: PropTypes.string,
  link: PropTypes.string,
  variant: PropTypes.string
};

/**
 * Footer component
 * Description: This component is used to display the footer.
 */
function Footer() {
  const { cloud } = useCloud();
  const [ open, setOpen ] = React.useState(false);
  const [ anchorEl, setAnchorEl ] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? "subscribe-popper" : undefined;
  const logo = window.ENV[cloud].logo;
  const footer = window.ENV[cloud].footer;
  const features = window.ENV[cloud].features;

  return (
    <Box
      sx={ {
        borderTopColor: "border.main",
        borderTopStyle: "solid",
        borderTopWidth: "1px",
        mt: 4
      } }
    >
      <Container maxWidth="md" sx={ { py: 4 } }>
        <Container>
          <Grid container spacing={ 2 } rowGap={ 2 } rowSpacing={ 2 } sx={ { mb: 4 } }>
            { /* Render the logo */ }
            <Grid item md={ 4 }>
              { logo.src ? (
                <img src={ logo.src } alt={ logo.alt } height={ footer.logo.height } width={ footer.logo.width } />
              ) : (
                <Typography variant="h4">{ logo.text }</Typography>
              ) }
            </Grid>

            { /* Render the privacy policy and terms of use */ }
            <Grid item md={ 4 }>
              <Typography variant="subtitle1" sx={ { mb: 2 } }>
                { footer.label }
              </Typography>
              <FooterItem
                link={ footer.privacy.url }
                label={ footer.privacy.label }
                variant="body2"
              />
              <FooterItem
                link={ footer.terms.url }
                label={ footer.terms.label }
                variant="body2" />
              { footer.serviceUptimes &&
                (<FooterItem
                  link={ footer.serviceUptimes.url }
                  label={ footer.serviceUptimes.label }
                  variant="body2"
                />)
              }
            </Grid>

            { /* Render the company links */ }
            <Grid item md={ 4 }>
              <Typography variant="subtitle1" sx={ { mb: 2 } }>
                Company
              </Typography>
              <FooterItem
                link="https://wso2.com/california-privacy#opt-out"
                label="Do Not Sell My Personal Information"
                variant="body2"
              />
              <FooterItem link="https://wso2.com/cookie-policy" label="Cookie Policy" variant="body2" />
              <FooterItem
                link="https://wso2.com/modern-slavery-statement"
                label="Modern Slavery Statement"
                variant="body2"
              />
            </Grid>
          </Grid>

          { /* Render the copyright and other related links */ }
          <Grid container spacing={ 2 } rowSpacing={ 2 }>
            <Grid item>
              <Typography variant="caption">&copy; { new Date().getFullYear() } WSO2 LLC</Typography>
            </Grid>
            <Grid item>
              <FooterItem link="https://wso2.com/terms-of-use" label="Terms" variant="caption" />
            </Grid>
            <Grid item>
              <FooterItem link="https://wso2.com/privacy-policy" label="Privacy Policy" variant="caption" />
            </Grid>
            <Grid item>
              <FooterItem
                link="mailto:human-response@wso2.com"
                label="Report a Problem With This Page"
                variant="caption"
              />
            </Grid>
            { features.subscribe && (
              <Grid item sx={ { marginLeft: "auto" } }>
                <Box>
                  <Link underline="none" variant="caption">
                    <Typography variant="caption">
                      <RssFeed fontSize="small" onClick={ handleClick } />
                    </Typography>
                  </Link>
                  { /* Render popper block */ }
                  <PopperBlock id={ id } open={ open } setOpen={ setOpen } anchorEl={ anchorEl } />
                </Box>
              </Grid>
            ) }
          </Grid>
        </Container>
      </Container>
    </Box>
  );
}

export default Footer;
